/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'
import {Controller, useFormContext} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormGrid from 'components/Forms/UiFormGrid'
import UiSelectWithGraphics from 'components/Ui/UiSelectWithGraphics'
import CompanyFormInput from 'pages/Front/Booking/components/CompanyForm/CompanyFormInput'
import CompanyContactAutocomplete from 'components/CompanyContactAutocomplete'
import CompanyPositionSelect from 'components/Companies/forms/CompanyPositionSelect'
import CompanyOriginSelect from 'components/Companies/forms/CompanyOriginSelect'
import ReservationDetailPhoneWithCountrySelect from 'pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/ReservationDetailPhoneWithCountrySelect'
import {filteredCountryList} from 'utils/countryUtils'
import {europeanCountryCodes} from 'utils/europeanCountries'
import {CompanyContact} from 'types/entities/CompanyContact'
import {Lead} from 'types/entities/Lead'
import {PhoneCountry} from 'utils/countryUtils'
import {LeadBasicInputs} from 'components/leads/schemas/useLeadBasicSchema'

interface LeadBasicFormContactSectionProps {
    compact: boolean
    selectedCompanyContact: CompanyContact | null
    companyContacts: CompanyContact[]
    highlightMissingContact: boolean
    effectiveLead: Lead | null | undefined
    isNewContact: boolean
    testField: (field: string) => boolean
    selectedContactCountry: PhoneCountry
    setSelectedContactCountry: (country: PhoneCountry) => void
    contactPhoneInput: string
    handleContactSelect: (contact: CompanyContact | null) => void
}

const LeadBasicFormContactSection: React.FC<LeadBasicFormContactSectionProps> = ({
    compact,
    selectedCompanyContact,
    companyContacts,
    highlightMissingContact,
    effectiveLead,
    isNewContact,
    testField,
    selectedContactCountry,
    setSelectedContactCountry,
    contactPhoneInput,
    handleContactSelect,
}) => {
    const {t} = useTranslation()
    const {register, control, formState: {errors}, setValue} = useFormContext<LeadBasicInputs>()

    return (
        <UiFormInner title={t('Account Contact')} className={clsx('LeadBasicFormContactSection h-full lg:order-3')}>
            <div className={clsx(highlightMissingContact && 'rounded-md ring-1 ring-red-400/60 p-2')}>
                <UiFormGrid cols={12} gap={6} responsive={false} className={clsx('grid-cols-12')}>
                    <div className={clsx('col-span-12 flex items-end gap-6')}>
                        <div className={clsx('min-w-0 flex-1')}>
                            <CompanyContactAutocomplete
                                label={t('Contact Person')}
                                name="contact_person"
                                placeholder={t('Search for existing contact...')}
                                onSelect={handleContactSelect}
                                initialContacts={companyContacts}
                                defaultValue={selectedCompanyContact}
                                showDropdownIcon={false}
                            />
                        </div>

                        <Controller
                            name="gender"
                            control={control}
                            render={({field}) => (
                                <UiSelectWithGraphics
                                    className={clsx('w-36 shrink-0')}
                                    label={t('Gender')}
                                    selected={field.value || ''}
                                    setSelected={(val) => field.onChange(val ?? undefined)}
                                    options={[
                                        {value: 'male', label: t('Male')},
                                        {value: 'female', label: t('Female')},
                                        {value: 'other', label: t('Other')},
                                    ]}
                                />
                            )}
                        />
                    </div>

                    {Boolean(selectedCompanyContact?.id && effectiveLead?.id) && (
                        <div className="col-span-12 text-xs text-amber-700 dark:text-amber-300">
                            {t('Will change the contact information globally')}
                        </div>
                    )}

                    <CompanyFormInput
                        className={clsx('col-span-6')}
                        label={t('First Name')}
                        name="first_name"
                        register={register}
                        errors={errors}
                        required={testField('first_name')}
                        placeholder={t('Enter first name')}
                        autoComplete="one-time-code"
                    />

                    <CompanyFormInput
                        className={clsx('col-span-6')}
                        label={t('Last Name')}
                        name="last_name"
                        register={register}
                        errors={errors}
                        required={testField('last_name')}
                        placeholder={t('Enter last name')}
                        autoComplete="one-time-code"
                    />

                    <div className={clsx('col-span-12 grid grid-cols-12 gap-6')}>
                        <CompanyFormInput
                            className={clsx('col-span-6')}
                            label={t('Email')}
                            name="contact_email"
                            type="email"
                            register={register}
                            errors={errors}
                            placeholder={t('company@example.com')}
                            required={isNewContact || testField('contact_email')}
                            autoComplete="one-time-code"
                        />

                        <ReservationDetailPhoneWithCountrySelect
                            className={clsx('col-span-6')}
                            options={filteredCountryList(europeanCountryCodes) as PhoneCountry[]}
                            defaultCountry={selectedContactCountry}
                            value={contactPhoneInput || ''}
                            register={register}
                            name="contact_phone"
                            label={t('Phone')}
                            setValue={(v: string) => setValue('contact_phone', v)}
                            setSelectedCountry={setSelectedContactCountry}
                            errors={errors}
                            required={isNewContact || testField('contact_phone')}
                        />
                    </div>

                    <Controller
                        name="position"
                        control={control}
                        render={({field}) => (
                            <CompanyPositionSelect
                                className={clsx('col-span-6')}
                                value={field.value || ''}
                                onChange={(value) => field.onChange(value ?? '', {shouldDirty: true})}
                                error={errors.position?.message}
                                required={testField('position')}
                                errors={errors}
                            />
                        )}
                    />

                    <Controller
                        name="contact_origin"
                        control={control}
                        render={({field}) => (
                            <CompanyOriginSelect
                                className={clsx('col-span-6')}
                                value={field.value || ''}
                                onChange={field.onChange}
                                error={errors.contact_origin?.message}
                                required={testField('contact_origin')}
                                errors={errors}
                            />
                        )}
                    />

                    <CompanyFormInput
                        className={clsx('col-span-12')}
                        label={t('Note')}
                        name="contact_note"
                        component="textarea"
                        register={register}
                        errors={errors}
                        required={testField('contact_note')}
                        placeholder={t('Enter note')}
                        autoComplete="one-time-code"
                    />
                </UiFormGrid>
            </div>
        </UiFormInner>
    )
}

export default LeadBasicFormContactSection
